import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import { MILESTONE_PRESENTATION } from '@/config/milestones';
import { milestoneBadgeLabel } from '@/utils/milestones';

const SIZES = {
    sm: { badge: 36, icon: 18, threshold: 11 },
    md: { badge: 52, icon: 24, threshold: 13 },
    lg: { badge: 68, icon: 30, threshold: 15 },
};

/**
 * Theme-safe milestone badge: allowlisted icon inside a token-colored disc
 * with the threshold it represents. Used in profile badges, org history,
 * the editor preview, and (Phase E) celebration entries.
 *
 * `colorToken` is always a themes.js color key resolved via the catalog
 * allowlist — never a raw hex string.
 */
const MilestoneBadge = ({
    icon = MILESTONE_PRESENTATION.defaultBadgeIcon,
    colorToken = MILESTONE_PRESENTATION.defaultBadgeColorToken,
    threshold = null,
    seriesName = null,
    earned = true,
    /** Highest earned tier in a series gets emphasized chrome. */
    emphasized = false,
    /** Archived/disabled historical badge — stays visible but subdued. */
    historical = false,
    size = 'md',
    showThreshold = true,
    onPress = null,
    testID,
    accessibilityLabel,
}) => {
    const dims = SIZES[size] ?? SIZES.md;
    const label = accessibilityLabel ?? milestoneBadgeLabel({ seriesName, threshold, earned });

    const disc = (
        <View style={styles.disc(dims.badge, colorToken, earned, emphasized, historical)}>
            <Unicon
                name={icon || MILESTONE_PRESENTATION.defaultBadgeIcon}
                size={dims.icon}
                colorVariant={earned ? colorToken : 'textSecondary'}
            />
        </View>
    );

    const content = (
        <View style={styles.container}>
            {disc}
            {showThreshold && threshold != null && (
                <Text style={styles.threshold(dims.threshold, colorToken, earned, historical)} numberOfLines={1}>
                    {threshold}
                </Text>
            )}
        </View>
    );

    if (!onPress) {
        return (
            <View testID={testID} accessibilityLabel={label} accessibilityRole="text">
                {content}
            </View>
        );
    }

    return (
        <Pressable testID={testID} accessibilityLabel={label} accessibilityRole="button" onPress={onPress} style={styles.pressable}>
            {content}
        </Pressable>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, shadows }) => ({
    container: {
        alignItems: 'center',
        gap: spacing.xs,
        minWidth: 40,
    },
    pressable: {
        _web: { cursor: 'pointer' },
    },
    disc: (badgeSize, colorToken, earned, emphasized, historical) => ({
        width: badgeSize,
        height: badgeSize,
        borderRadius: badgeSize / 2,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: earned ? (colors[`${colorToken}Container`] || colors.primaryContainer) : colors.surfaceVariant,
        borderWidth: emphasized ? 2 : 0,
        borderColor: emphasized ? (colors[colorToken] || colors.primary) : 'transparent',
        boxShadow: earned ? shadows.card : 'none',
        opacity: historical ? 0.7 : 1,
    }),
    threshold: (fontSize, colorToken, earned, historical) => ({
        fontSize,
        fontFamily: fonts.bold,
        color: earned ? (colors[colorToken] || colors.primary) : colors.textSecondary,
        opacity: historical ? 0.7 : 1,
    }),
}));

export default MilestoneBadge;
